import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router'
import { formatDate } from 'BrokerTsLib/src/Utils'

import { getInsurerOfferCategoryName, PolicyStatusType } from 'BrokerTsLib/src/enums'
import type { CancelledContractItem } from '@/hooks/useWizard'

type Props = {
  contracts: CancelledContractItem[]
  isOpen: boolean
  onClose: () => void
  /** Pokud je zadáno, zobrazí tlačítko pro pokračování na detail skladové smlouvy. */
  stockDetailUrl?: string
}

const contractDetailUrl = (c: CancelledContractItem) =>
  c.insurerOfferId
    ? `/leasing/case/${c.insuranceParamsId}/steps/complete/${c.insurerOfferId}`
    : `/leasing/steps/complete/${c.insuranceParamsId}`

type StatusInfo = { label: string; color: string }

const getStatusInfo = (c: CancelledContractItem): StatusInfo => {
  if (c.policyStatus === PolicyStatusType.PendingCancellation)
    return { label: c.endDate ? `Bude stornována k ${formatDate(new Date(c.endDate))}` : 'Bude stornována', color: 'orange' }
  if (c.policyStatus === PolicyStatusType.CancellationInProgress)
    return { label: 'Probíhá storno', color: 'orange' }
  if (c.cancelled || c.policyStatus === PolicyStatusType.Cancelled)
    return { label: 'Stornováno', color: 'green' }
  return { label: 'Nestornováno', color: 'red' }
}

export const CancelledContractsModal = ({ contracts, isOpen, onClose, stockDetailUrl }: Props) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Storno smluv</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack gap={4}>
            <Text fontSize="sm" color="gray.600">
              Byl proveden pokus o storno následujících smluv.
            </Text>
            <Divider />
            <VStack align="stretch" gap={3}>
              {contracts.map(c => {
                const name = getInsurerOfferCategoryName(c.category)
                const status = getStatusInfo(c)
                const detailUrl = contractDetailUrl(c)
                // proklik jen u smluv, které míří jinam, než kam ukazuje aktuální URL
                const isCurrent = detailUrl === pathname
                return (
                  <Box key={c.insuranceId}>
                    <Flex align="center" gap={2}>
                      {isCurrent ? (
                        <Text fontWeight="medium">
                          {c.contractNumber}
                          {name && <Text as="span" color="gray.500" ml={2}>({name})</Text>}
                        </Text>
                      ) : (
                        <Link as={RouterLink} to={detailUrl} color="primary.500" fontWeight="medium">
                          {c.contractNumber}
                          {name && <Text as="span" color="gray.500" ml={2}>({name})</Text>}
                        </Link>
                      )}
                      <Badge colorScheme={status.color}>{status.label}</Badge>
                    </Flex>
                    {c.errors?.length > 0 && (
                      <VStack align="stretch" gap={0} mt={1}>
                        {c.errors.map((e, i) => (
                          <Text key={i} fontSize="sm" color="red.500">{e}</Text>
                        ))}
                      </VStack>
                    )}
                  </Box>
                )
              })}
            </VStack>
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          {stockDetailUrl ? (
            <Button colorScheme="blue" onClick={() => navigate(stockDetailUrl)}>
              Pokračovat na detail smlouvy
            </Button>
          ) : (
            <Button colorScheme="blue" onClick={onClose}>
              Zavřít
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
